import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { useFx } from "@/lib/fx/fxTier";

/**
 * Primitivos Rocketanks.
 *
 * Antes cada uma das 23 views do menu reimplementava o mesmo botão
 * gradiente, o mesmo painel translúcido e o mesmo cabeçalho à mão —
 * qualquer refinamento visual virava 23 edições. Aqui a decisão é
 * tomada uma vez; as telas só compõem.
 *
 * Regra de custo: nenhum primitivo aplica blur, glow ou animação
 * fora dos tokens. Quem governa o orçamento é `data-fx` no <html>.
 */

/* ============================================================
   Screen — casca de tela cheia
   ============================================================ */

interface ScreenProps {
  children: React.ReactNode;
  /** Largura máxima do conteúdo. `wide` para grids, `full` para hubs. */
  width?: "narrow" | "wide" | "full";
  className?: string;
  contentClassName?: string;
  /** Espaço extra embaixo para a dock não cobrir o conteúdo. */
  dockPadding?: boolean;
  /**
   * `false` quando o conteúdo gerencia o próprio scroll (listas
   * virtualizadas, painéis com abas fixas). Dois contêineres roláveis
   * aninhados brigam pelo gesto de toque no mobile.
   */
  scroll?: boolean;
}

/**
 * Em paisagem de telefone a coluna cresce.
 *
 * `max-w-md` num viewport de 844px deixava ~400px de vazio de cada
 * lado e empilhava tudo nos 390px de altura que sobram. Deitado, a
 * largura é o recurso abundante e a altura é o escasso — a caixa
 * acompanha, e as pilhas de cards viram duas colunas com `rk-stack`.
 */
const WIDTHS = {
  narrow: "max-w-md short:max-w-3xl lg:max-w-lg",
  wide: "max-w-md short:max-w-4xl lg:max-w-3xl xl:max-w-5xl",
  full: "max-w-none",
} as const;

export const Screen = forwardRef<HTMLDivElement, ScreenProps>(function Screen(
  { children, width = "narrow", className, contentClassName, dockPadding = true, scroll = true },
  ref,
) {
  const tier = useFx((s) => s.tier);
  const reducedMotion = useFx((s) => s.reducedMotion);
  // Faixa de luz atravessa a tela ao abrir. Só no tier alto: é puro
  // deleite, o primeiro a sair quando há orçamento apertado.
  const sweep = tier === "high" && !reducedMotion;

  return (
    <div
      ref={ref}
      data-menu-root
      className={cn(
        "fixed inset-0 z-50 flex flex-col overflow-hidden safe-area-top safe-area-bottom",
        sweep && "rk-sweep",
        className,
      )}
    >
      <div
        className={cn(
          // `short` = telefone deitado. Só ~380px de altura útil, então
          // o respiro vertical encolhe e o colchão da dock acompanha a
          // dock, que também encolhe.
          "relative z-10 px-4 py-5 short:py-2",
          scroll
            ? "flex-1 overflow-y-auto overscroll-contain scroll-smooth-ios"
            : "flex min-h-0 flex-1 flex-col overflow-hidden",
          scroll && dockPadding && "pb-28 short:pb-16",
        )}
      >
        <div
          className={cn(
            "mx-auto w-full",
            !scroll && "flex min-h-0 flex-1 flex-col",
            WIDTHS[width],
            contentClassName,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
});

/* ============================================================
   ScreenHeader — título + voltar, idêntico em toda tela
   ============================================================ */

interface ScreenHeaderProps {
  title: string;
  subtitle?: string;
  /** Etiqueta acima do título (ex.: "BETA", "NOVO"). */
  eyebrow?: string;
  onBack?: () => void;
  backLabel?: string;
  /** Conteúdo à direita (badges, ações). */
  actions?: React.ReactNode;
  align?: "left" | "center";
}

export function ScreenHeader({
  title,
  subtitle,
  eyebrow,
  onBack,
  backLabel = "Voltar",
  actions,
  align = "left",
}: ScreenHeaderProps) {
  return (
    // `flex-wrap` + `w-full` nas ações abaixo de 420px: com a barra de
    // moedas no slot de ações, um telefone de 320px deixava 86px para o
    // título — "Escolha o Modo" virava "Escolha…". Numa linha própria as
    // ações cabem sem comer o título.
    <header
      className={cn(
        "mb-6 flex flex-wrap items-start gap-3 short:mb-3",
        align === "center" && "flex-col items-center text-center",
      )}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label={backLabel}
          className="rk-blur-sm mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-rk-md border border-stroke text-ink transition-colors duration-fast ease-rk hover:border-stroke-strong active:scale-95"
          style={{ background: "var(--rk-surface-1)" }}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      <div className={cn("min-w-0 flex-1 basis-40", align === "center" && "flex flex-col items-center")}>
        {eyebrow && (
          <span className="mb-1.5 inline-block rounded-rk-pill border border-stroke-accent bg-rk-accent/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rk-accent">
            {eyebrow}
          </span>
        )}
        {/* `line-clamp-2` em vez de `truncate`: o título de uma tela é
            a resposta para "onde eu estou". Quebrar em duas linhas custa
            ~28px; cortar em "Escolha o Mod…" custa a resposta. */}
        <h1 className="line-clamp-2 bg-gradient-to-r from-cyan-300 via-sky-200 to-fuchsia-300 bg-clip-text text-2xl font-bold text-transparent short:text-xl lg:text-3xl">
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-ink-muted short:hidden lg:block">{subtitle}</p>}
      </div>

      {actions && (
        <div className="flex shrink-0 items-center gap-2 max-[420px]:order-last max-[420px]:w-full max-[420px]:justify-end">
          {actions}
        </div>
      )}
    </header>
  );
}

/* ============================================================
   Panel — superfície translúcida padrão
   ============================================================ */

interface PanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  /** `sunken` para áreas de leitura dentro de outro painel. */
  tone?: "raised" | "sunken" | "accent";
  padding?: "none" | "sm" | "md" | "lg";
  blur?: boolean;
}

const PANEL_PADDING = { none: "", sm: "p-3", md: "p-4", lg: "p-5 lg:p-6" } as const;

export function Panel({ children, tone = "raised", padding = "md", blur = true, className, ...rest }: PanelProps) {
  const background =
    tone === "sunken" ? "var(--rk-surface-sunken)" : tone === "accent" ? "rgba(34, 211, 238, 0.08)" : "var(--rk-surface-1)";

  return (
    <div
      {...rest}
      className={cn(
        "rounded-rk-lg border",
        tone === "accent" ? "border-stroke-accent" : "border-stroke",
        tone !== "sunken" && "shadow-elev-2",
        blur && tone !== "sunken" && "rk-blur-sm",
        PANEL_PADDING[padding],
        className,
      )}
      style={{ background, ...rest.style }}
    >
      {children}
    </div>
  );
}

/* ============================================================
   ActionCard — o botão grande de ação (era copiado ~40x)
   ============================================================ */

export type ActionTone = "primary" | "accent" | "violet" | "amber" | "danger" | "neutral";

const TONES: Record<ActionTone, { from: string; to: string; glow: string; ring: string }> = {
  primary: { from: "#059669", to: "#10b981", glow: "16, 185, 129", ring: "rgba(16,185,129,.45)" },
  accent: { from: "#0891b2", to: "#22d3ee", glow: "34, 211, 238", ring: "rgba(34,211,238,.45)" },
  violet: { from: "#7c3aed", to: "#a855f7", glow: "168, 85, 247", ring: "rgba(168,85,247,.45)" },
  amber: { from: "#d97706", to: "#fbbf24", glow: "251, 191, 36", ring: "rgba(251,191,36,.45)" },
  danger: { from: "#dc2626", to: "#fb7185", glow: "251, 113, 133", ring: "rgba(251,113,133,.45)" },
  neutral: { from: "rgba(40,52,80,.9)", to: "rgba(28,38,62,.9)", glow: "148, 176, 224", ring: "rgba(148,176,224,.3)" },
};

interface ActionCardProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "title"> {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  tone?: ActionTone;
  size?: "sm" | "md" | "lg";
  badge?: React.ReactNode;
  /** Seta indicando que abre um submenu. */
  chevron?: boolean;
  /** Passe `false` para o brilho que percorre o card no hover. */
  shimmer?: boolean;
  /** Linha extra abaixo do conteúdo — normalmente `Pill`s de destaque. */
  footer?: React.ReactNode;
}

const ACTION_SIZES = {
  sm: "px-4 py-3 text-sm",
  md: "px-5 py-4 text-base short:py-3",
  // O card primário do hub. Em paisagem ele sozinho comia ~1/5 da
  // altura útil; encolhe para o tamanho `md` sem perder o alvo de toque.
  lg: "px-6 py-5 text-lg short:py-3 short:text-base lg:text-xl",
} as const;

/** Espaçamento entre ícone, texto e afixos — separado do padding
 *  porque a linha interna é que aplica o gap, não o botão. */
const ACTION_GAPS = { sm: "gap-3", md: "gap-3.5", lg: "gap-4" } as const;

export const ActionCard = forwardRef<HTMLButtonElement, ActionCardProps>(function ActionCard(
  { title, description, icon, tone = "neutral", size = "md", badge, chevron, shimmer = true, footer, className, ...rest },
  ref,
) {
  const palette = TONES[tone];

  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        "rk-action group relative flex w-full min-h-touch flex-col overflow-hidden rounded-rk-lg text-left font-bold text-white",
        "border border-white/10 transition-transform duration-fast ease-rk",
        "active:scale-[.985] disabled:pointer-events-none disabled:opacity-45",
        ACTION_SIZES[size],
        className,
      )}
      style={
        {
          background: `linear-gradient(135deg, ${palette.from}, ${palette.to})`,
          "--rk-action-glow": palette.glow,
          "--rk-action-ring": palette.ring,
          ...rest.style,
        } as React.CSSProperties
      }
    >
      {/* Glow em camada própria: animar a opacidade dela não repinta o
          card, ao contrário de animar box-shadow no elemento. */}
      <span className="rk-action__glow" aria-hidden="true" />
      {shimmer && <span className="rk-action__shimmer" aria-hidden="true" />}

      <span className={cn("relative flex w-full items-center", ACTION_GAPS[size])}>
        {icon && <span className="shrink-0 text-2xl leading-none lg:text-3xl">{icon}</span>}

        <span className="min-w-0 flex-1">
          <span className="block truncate">{title}</span>
          {description && <span className="mt-0.5 block text-xs font-normal text-white/70 lg:text-sm">{description}</span>}
        </span>

        {badge && <span className="shrink-0">{badge}</span>}
        {chevron && (
          <svg className="w-5 shrink-0 opacity-60 transition-transform duration-fast ease-rk group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        )}
      </span>

      {footer && <span className="relative mt-2.5 flex w-full flex-wrap items-center gap-1.5">{footer}</span>}
    </button>
  );
});

/* ============================================================
   TileButton — card compacto das faixas horizontais
   ============================================================ */

interface TileButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  label: string;
  tone?: ActionTone;
  badge?: React.ReactNode;
  /** Sobreposições posicionadas (barra de progresso, contador…). */
  children?: React.ReactNode;
}

export const TileButton = forwardRef<HTMLButtonElement, TileButtonProps>(function TileButton(
  { icon, label, tone = "neutral", badge, children, className, ...rest },
  ref,
) {
  const palette = TONES[tone];
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        // Largura fixa enquanto a faixa rola no mobile; no grid (sm+) ocupa a célula
        "rk-action group relative flex w-28 shrink-0 flex-col items-center justify-center gap-1.5 overflow-hidden rounded-rk-lg px-3 py-4 sm:w-full",
        "min-h-touch border border-white/10 font-bold text-white transition-transform duration-fast ease-rk active:scale-95",
        className,
      )}
      style={
        {
          background: `linear-gradient(160deg, ${palette.from}, ${palette.to})`,
          "--rk-action-glow": palette.glow,
          "--rk-action-ring": palette.ring,
        } as React.CSSProperties
      }
    >
      <span className="rk-action__glow" aria-hidden="true" />
      <span className="relative text-2xl leading-none lg:text-3xl">{icon}</span>
      <span className="relative text-center text-xs leading-tight lg:text-sm">{label}</span>
      {children}
      {badge && <span className="absolute -right-1 -top-1 z-10">{badge}</span>}
    </button>
  );
});

/* ============================================================
   StatChip / Badge
   ============================================================ */

interface StatChipProps {
  label?: React.ReactNode;
  value: React.ReactNode;
  icon?: React.ReactNode;
  tone?: "neutral" | "positive" | "warning" | "danger" | "accent";
  className?: string;
}

const CHIP_TONES = {
  neutral: "text-ink-muted",
  positive: "text-rk-positive",
  warning: "text-rk-warning",
  danger: "text-rk-danger",
  accent: "text-rk-accent",
} as const;

export function StatChip({ label, value, icon, tone = "neutral", className }: StatChipProps) {
  return (
    <div
      className={cn("rounded-rk-md border border-stroke px-2.5 py-1.5 text-center", className)}
      style={{ background: "var(--rk-surface-sunken)" }}
    >
      <div className={cn("flex items-center justify-center gap-1 text-sm font-bold tabular-nums", CHIP_TONES[tone])}>
        {icon}
        {value}
      </div>
      {label && <div className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-faint">{label}</div>}
    </div>
  );
}

interface PillProps {
  children: React.ReactNode;
  tone?: "neutral" | "positive" | "warning" | "danger" | "accent" | "violet";
  className?: string;
}

const PILL_TONES = {
  neutral: "border-stroke bg-white/5 text-ink-muted",
  positive: "border-emerald-400/35 bg-emerald-400/12 text-emerald-300",
  warning: "border-amber-400/35 bg-amber-400/12 text-amber-300",
  danger: "border-rose-400/35 bg-rose-400/12 text-rose-300",
  accent: "border-cyan-400/35 bg-cyan-400/12 text-cyan-300",
  violet: "border-violet-400/35 bg-violet-400/12 text-violet-300",
} as const;

export function Pill({ children, tone = "neutral", className }: PillProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-rk-pill border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
        PILL_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ============================================================
   Toggle — interruptor de ajuste
   ============================================================ */

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Rótulo acessível quando o texto vive fora do controle. */
  label: string;
  disabled?: boolean;
  className?: string;
}

/**
 * O knob se move por `translate-x`, não por `left`.
 *
 * Além de ser trabalho de composição em vez de reflow, isto conserta um
 * bug silencioso: os toggles antigos declaravam `transition-transform`
 * mas animavam `left`, então a transição nunca se aplicava e o knob
 * saltava de um lado ao outro sem animação nenhuma.
 */
export function Toggle({ checked, onChange, label, disabled = false, className }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(
        "relative h-7 w-12 shrink-0 rounded-rk-pill transition-colors duration-base ease-rk",
        checked ? "bg-rk-accent" : "bg-slate-600",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        className,
      )}
    >
      <span
        className={cn(
          "absolute left-1 top-1 block h-5 w-5 rounded-full bg-white shadow-elev-1",
          "transition-transform duration-base ease-rk",
          checked ? "translate-x-5" : "translate-x-0",
        )}
      />
    </button>
  );
}

/* ============================================================
   Spinner / LoadingState — espera com feedback anunciado
   ============================================================ */

const SPINNER_SIZES = {
  sm: "h-5 w-5 border-2",
  md: "h-8 w-8 border-2",
  lg: "h-12 w-12 border-4",
} as const;

/**
 * Anel de carregamento.
 *
 * `rk-motion-essential` marca o giro como feedback funcional: o
 * kill-switch de movimento reduzido isenta esta classe, senão o anel
 * congela e passa a dizer "travou" em vez de "carregando". Com
 * movimento reduzido o CSS ainda desacelera o giro.
 *
 * O elemento em si é `aria-hidden`: quem anuncia é o `LoadingState`
 * em volta, ou o `role="status"` de quem o compõe. Dois anúncios para
 * o mesmo estado é ruído no leitor de tela.
 */
export function Spinner({ size = "md", className }: { size?: "sm" | "md" | "lg"; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "rk-motion-essential inline-block shrink-0 animate-spin rounded-full border-current border-t-transparent",
        SPINNER_SIZES[size],
        className,
      )}
    />
  );
}

interface LoadingStateProps {
  /** Texto visível e anunciado. Sempre traduzido pelo chamador. */
  label: string;
  size?: "sm" | "md" | "lg";
  /** `false` esconde o texto, mantendo-o só para o leitor de tela. */
  showLabel?: boolean;
  className?: string;
  labelClassName?: string;
}

/** Espera centralizada: anel + rótulo, anunciado como `status`. */
export function LoadingState({ label, size = "md", showLabel = true, className, labelClassName }: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("flex flex-col items-center justify-center gap-3 text-rk-accent", className)}
    >
      <Spinner size={size} />
      <span className={cn("text-sm font-medium", labelClassName, !showLabel && "sr-only")}>{label}</span>
    </div>
  );
}

/* ============================================================
   SectionTitle — cabeçalho das faixas do hub
   ============================================================ */

export function SectionTitle({ icon, children, action }: { icon?: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="mb-2.5 flex items-center justify-between gap-2 px-0.5">
      <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-ink-faint">
        {icon}
        {children}
      </h2>
      {action}
    </div>
  );
}
